import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { LoginLibService } from '@ebootis/login';
import { StorageService } from '@ebootis/utilities';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilKeyChanged, filter, takeUntil } from 'rxjs/operators';
import { ConfigurationService } from '../components/configuration-select/configuration.service';
import { ArticleBasketItemByOid } from '../services/Ebootis.Queries/ArticleBasketItemByOid';
import { ArticleDetailsByOid } from './Ebootis.Queries/ArticleDetailsByOid';

/**
* Custom Validator um zu überprüfen ob die Menge nicht das freie Kontingent @param p_freeContingent übersteigt
* Als importierbare globale Funktion deklariert da, noch an anderer Stelle verwendung vorhanden ist.
* @param p_freeContingent freie Kontingent
* @returns null oder freeContingent Error
*/
export function createFreeContingentValidator(p_freeContingent: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value && !isNaN(value)) {
      return null;
    }
    return value > p_freeContingent ? { freeContingent: true } : null;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ArticleBasketService {

  private m_selectedArticle: ArticleBasketItemByOid.Result;
  private m_basketItems: ArticleBasketItemByOid.Result[] = this.getBasketItemsFromStorage();
  private storageName: string;
  private basketItemBehavior: BehaviorSubject<any> = new BehaviorSubject(this.m_basketItems);
  public basketItems: Observable<any> = this.basketItemBehavior.asObservable();
  private m_subscriptionsSubject = new Subject();

  constructor(
    private m_basketItemDetails: ArticleBasketItemByOid,
    private m_cfg: ConfigurationService,
    private m_loggedInService: LoginLibService,
    private storageService: StorageService,
    private m_translateService: TranslateService
  ) {
    this.m_cfg.getConfiguration()
      .pipe(filter((v) => v != null))
      .pipe(distinctUntilKeyChanged('oid'))
      .pipe(takeUntil(this.m_subscriptionsSubject))
      .subscribe({
        next: (cfg) => {
          if (this.m_loggedInService.LoggedIn && this.m_cfg.SelectedConfigOid) {
            this.storageName = `${this.m_cfg.SelectedConfigOid}/ZBSbasketItems`
            this.m_basketItems = this.getBasketItemsFromStorage();
            this.basketItemBehavior.next(this.m_basketItems);
          } else {
            this.storageName = null;
            this.m_basketItems = []
            this.basketItemBehavior.next(this.m_basketItems);
          }
        }
      })
  }

  private addItemToList(p_item: ArticleBasketItemByOid.Result) {
    let idx = this.m_basketItems.findIndex((el) => el.oid == p_item.oid);
    if (idx != -1) {
      this.m_basketItems[idx] = p_item;
    } else {
      this.m_basketItems.push(p_item);
    }
    let string = JSON.stringify(this.m_basketItems);
    this.storageService.setItem('local', this.storageName, string);
  }
  public removeBasketItem(p_articleOid: string): void {
    this.m_basketItems = this.m_basketItems.filter((el) => el.oid != p_articleOid);
    this.basketItemBehavior.next(this.m_basketItems);
    this.storageService.setItem('local', this.storageName, JSON.stringify(this.m_basketItems));

  }
  private getBasketItemsFromStorage(): ArticleBasketItemByOid.Result[] {
    const storageObject = this.storageService.getItem('local', this.storageName);
    const jsonObj = storageObject ? JSON.parse(storageObject) : null;
    return jsonObj && jsonObj instanceof Array ? jsonObj : []
  }
  private getBasketItem(p_articleOid: string): ArticleBasketItemByOid.Result {
    return this.m_basketItems ? this.m_basketItems.find((el) => el.oid == p_articleOid) : null;
  }
  /**
   * speichert einen Artikel im Warenkorb,
   * wirft einen Error bei Fehlern wie zu niedriger Menge.
   */
  async saveBasketItem(p_articleOid: string, p_configOid: string, p_itemAmount?: number): Promise<ArticleBasketItemByOid.Result> {
    // Menge optional. Standardmäßig 1
    if (p_itemAmount == null) {
      p_itemAmount = 1;
    }
    await this.getArticleBasketDetailsByOid(p_articleOid, p_configOid, p_itemAmount).toPromise().then((res: ArticleBasketItemByOid.Result) => {
      // neuer Artikel im WK
      this.m_selectedArticle = res;
      let existingBasketItem = this.getBasketItem(p_articleOid);
      if (existingBasketItem) {
        existingBasketItem = { ...existingBasketItem };
        existingBasketItem.quantity += res.quantity;
        existingBasketItem.customerPrice = res.customerPrice;
        this.m_selectedArticle = existingBasketItem;
      }
      // Plausibilitätsprüfungen für die Bestellung
      // Entweder ist das Flag onlyAvailableStock nicht gesetzt oder es wird gegen die Verfügbare Menge geprüft
      if (!this.m_selectedArticle.isStockArticle || (!this.m_selectedArticle.onlyAvailableStock || this.m_selectedArticle.quantity <= (this.m_selectedArticle.freeContingent || this.m_selectedArticle.availableNow))) {
        if (this.m_selectedArticle.quantity < 1) {
          throw new Error(this.m_translateService.instant('GLOBAL.ERROR.ARTICLE_AVAILABLE.TEXT'));

        } else if (this.m_selectedArticle.quantity < this.m_selectedArticle.salesAmountMin) {
          throw new Error(this.m_translateService.instant('GLOBAL.ERROR.ARTICLE_MIN.TEXT', { minimum: this.m_selectedArticle.salesAmountMin }));
        } else if (this.m_selectedArticle.quantity > this.m_selectedArticle.salesAmountMax) {
          throw new Error(this.m_translateService.instant('GLOBAL.ERROR.ARTICLE_MAX.TEXT', { maximum: this.m_selectedArticle.salesAmountMax }));
        } else {
          this.addItemToList(this.m_selectedArticle);
          this.basketItemBehavior.next(Array.from(this.m_basketItems.values()));
        }
      } else {
        throw new Error(this.m_translateService.instant('GLOBAL.ERROR.ARTICLE_AVAILABLE.TEXT'));
      }
    }).catch((error) => {
      // Rückgabe wenn negativ Plausibel
      return Promise.reject(error);
    })
    // Rückgabe wenn positiv Plausibel
    return this.m_selectedArticle;
  }



  /**Liefert die Anzahl der Warenkorbpositionen */
  get calculateBasketItemCount(): number {
    return this.m_basketItems.length;
  }

  /** löscht die basketItems aus dem Cache und leert die basketItems-Objekte */
  public deleteBasket(): void {
    this.storageService.removeItem('local', this.storageName);
    this.m_basketItems = [];
    this.basketItemBehavior.next(Array.from(this.m_basketItems.values()));
  }

  public getArticleBasketDetailsByOid(p_articleOid: string, p_config, p_amount): Observable<ArticleBasketItemByOid.Result> {
    return this.m_basketItemDetails.query({ article: p_articleOid, config: p_config, quantity: p_amount });
  }

  public getQuantityValidators(p_basketItem: ArticleBasketItemByOid.Result | ArticleDetailsByOid.Result): ValidatorFn[] {
    let quantityValidators = [Validators.required, Validators.min(p_basketItem.salesAmountMin ? p_basketItem.salesAmountMin : 1)];
    // Prüfung auf onlyAvailableStock oder salesAmountMax.
    if (p_basketItem.onlyAvailableStock) {
      if (p_basketItem.salesAmountMax && p_basketItem.salesAmountMax < p_basketItem.availableNow) {
        quantityValidators.push(Validators.max(p_basketItem.salesAmountMax));
      } else {
        if (p_basketItem.availableNow === 0 && p_basketItem.freeContingent > 0)
          quantityValidators.push(Validators.max(p_basketItem.freeContingent));
        else if (p_basketItem.availableNow < p_basketItem.freeContingent)
          quantityValidators.push(Validators.max(p_basketItem.freeContingent));
        else
          quantityValidators.push(Validators.max(p_basketItem.availableNow));
      }
    } else if (p_basketItem.salesAmountMax) {
      quantityValidators.push(Validators.max(p_basketItem.salesAmountMax));
    }
    if (p_basketItem?.freeContingent != undefined)
      quantityValidators.push(createFreeContingentValidator(p_basketItem.freeContingent));
    return quantityValidators;
  }



  public updateArticleLocalStorageQuantity(p_articleOid: string, p_quantity: number) {
    const basketItem = this.getBasketItem(p_articleOid);
    if (basketItem) {
      basketItem.quantity = p_quantity;
    }
    this.addItemToList(basketItem);
  }

  public getMinimumAmountError(p_amount: number): string {
    return this.m_translateService.instant('GLOBAL.ERROR.ARTICLE_MIN.TEXT', { minimum: p_amount });
  }

  public getMaximumAmountError(p_amount: number): string {
    return this.m_translateService.instant('GLOBAL.ERROR.ARTICLE_MAX.TEXT', { maximum: p_amount });
  }

}
